import { NGXLogger } from 'ngx-logger';
import { Injectable } from '@angular/core';

import { CommsService } from '@services/comms/comms.service';
import { Events } from '@services/events/events.service';
import { BaseService } from './../abstract-base-service/abstract-base-service.service';
import { Asset, AssetEventsCache, AssetFilterParameters, HealthEvent, IAssetPropertyValue } from './asset.interfaces';
import { IObjectStringKeyMap } from '@shared/models';

import { TranslateService } from '@ngx-translate/core';
import { FoldersDataService } from '@services/folders/folders-data.service';
import { cloneDeep, each, filter, find, get, includes, isArray, isNumber, isUndefined, keyBy, some, sortBy, union } from 'lodash';
import { LoadingService } from '@services/loading/loading.service';
import { awaitHandler } from '@utils/awaitHandler';
import { DataTableOptions } from '@services/table/table.service';
import { UserService } from '@services/user/user.service';
import { PeriodDateRange } from '@modules/observation/pages/asset-status/pages/detail/model/asset-state.interfaces';

export enum AssetState {
  Up = 2,
  Strained = 1,
  Down = 0,
  NoState = -1
}

@Injectable({
  providedIn: 'root'
})
export class AssetsService extends BaseService {
  asset: {
    data: Asset[];
    lastRequest: number | null;
  } = {
    data: [],
    lastRequest: null
  };

  private _pendingRefresh: Promise<any[]> | null = null;
  private _pendingRefreshMaxUpdated = 0;
  private _pendingLiteRefresh: Promise<any[]> | null = null;
  private _pendingLiteRefreshMaxUpdated = 0;
  // CVX-5984: per-(assetID, windowKey) incremental event cache
  private _eventsCache = new Map<string, AssetEventsCache>();
  // CVX-5984: in-flight Promise dedup for getAssetEvents
  private _eventsInFlight = new Map<string, Promise<Asset>>();

  constructor(
    private logger: NGXLogger,
    protected commsService: CommsService,
    private events: Events,
    private translate: TranslateService,
    private folderService: FoldersDataService,
    private loadingService: LoadingService,
    private userService: UserService
  ) {
    super(commsService);
  }

  public refresh(updated: number = 0): Promise<any[]> {
    if (updated && updated <= this.asset.lastRequest) {
      this.logger.log(`local assets cache already up to date: ${updated}, ${this.asset.lastRequest}`);
      return Promise.resolve(sortBy(this.asset.data, 'assetID'));
    }
    if (this._pendingRefresh) {
      // Track the highest 'updated' time requested while this refresh is in flight,
      // so we can fire a trailing-edge re-issue if the in-flight result is older than
      // the latest SSE event that arrived during the call.
      if (updated > this._pendingRefreshMaxUpdated) {
        this._pendingRefreshMaxUpdated = updated;
      }
      this.logger.log('getAssets already in flight — returning pending promise');
      return this._pendingRefresh;
    }
    this._pendingRefreshMaxUpdated = updated;
    this._pendingRefresh = new Promise<any[]>((resolve, reject) => {
      const when = Date.now();
      let succeeded = false;
      // R2 F-01: compute incremental intent ONCE so the request flag and the
      // updateCache discriminator cannot drift.
      const incremental = !!this.asset.lastRequest;
      this.commsService.sendMessage({
        cmd: 'getAssets',
        includeDisabled: 1,
        ...(incremental ? { incremental: 1 } : {}),
        lastRequest: this.asset.lastRequest,
        sendTime: when
      }, false, false).then(data => {
        if (data && data.reqStatus === 'OK') {
          this.updateCache(data, incremental);
          succeeded = true;
        }
        resolve(sortBy(this.asset.data, 'assetID'));
      }).catch((err) => {
        reject(err);
      }).finally(() => {
        const trailingUpdated = this._pendingRefreshMaxUpdated;
        this._pendingRefresh = null;
        this._pendingRefreshMaxUpdated = 0;
        // Only fire a trailing-edge refresh after a successful in-flight call.
        // If the in-flight call failed, lastRequest is unchanged and re-issuing
        // immediately would amplify failure traffic during outages. .catch() the
        // trailing call so any rejection from the recursive refresh is swallowed
        // here (no unhandled-rejection noise).
        if (succeeded && trailingUpdated && trailingUpdated > (this.asset.lastRequest ?? 0)) {
          this.refresh(trailingUpdated).catch(() => { /* trailing refresh failure logged via err handler in the recursed call */ });
        }
      });
    });
    return this._pendingRefresh;
  }

  public liteRefresh(updated: number = 0): Promise<any[]> {
    if (updated && updated <= this.asset.lastRequest) {
      return Promise.resolve(sortBy(this.asset.data, 'assetID'));
    }
    if (this._pendingLiteRefresh) {
      if (updated > this._pendingLiteRefreshMaxUpdated) {
        this._pendingLiteRefreshMaxUpdated = updated;
      }
      this.logger.log('getAssets (lite) already in flight — returning pending promise');
      return this._pendingLiteRefresh;
    }
    this._pendingLiteRefreshMaxUpdated = updated;
    this._pendingLiteRefresh = new Promise<any[]>((resolve, reject) => {
      const when = Date.now();
      let succeeded = false;
      // R2 F-01: compute incremental intent ONCE so the request flag and the
      // updateCache discriminator cannot drift.
      const incremental = !!this.asset.lastRequest;
      this.commsService.sendMessage({
        cmd: 'getAssets',
        includeDisabled: 1,
        includePropertyValues: 0,
        ...(incremental ? { incremental: 1 } : {}),
        lastRequest: this.asset.lastRequest,
        sendTime: when
      }, false, false).then(data => {
        if (data && data.reqStatus === 'OK') {
          this.updateCache(data, incremental);
          succeeded = true;
        }
        resolve(sortBy(this.asset.data, 'assetID'));
      }).catch((err) => {
        reject(err);
      }).finally(() => {
        const trailingUpdated = this._pendingLiteRefreshMaxUpdated;
        this._pendingLiteRefresh = null;
        this._pendingLiteRefreshMaxUpdated = 0;
        if (succeeded && trailingUpdated && trailingUpdated > (this.asset.lastRequest ?? 0)) {
          this.liteRefresh(trailingUpdated).catch(() => { /* swallow trailing failure */ });
        }
      });
    });
    return this._pendingLiteRefresh;
  }

  /**
   * Fetch property/health events for an asset, with optional cursor-based
   * incremental cache (CVX-5984 Phase 2).
   *
   * Behavior:
   * - First call for a window: full `startTime`/`endTime` (seconds) +
   *   `includePriorState=1`. Server's response cursors (`maxPropertyEventID` /
   *   `maxHealthEventID`) and merged events are cached under
   *   `windowKey(assetId, range)`.
   * - Subsequent calls for the same window with at least one cached cursor:
   *   incremental request via `propertyEventsSinceID`/`healthEventsSinceID`
   *   (the side without a cursor still fetches the full slice). Delta merged
   *   into the cache; rolling-window lower bound recomputed from
   *   `Date.now() - originalDurationMs` at trim time.
   * - If `range` is falsy or its bounds are missing, falls back to an
   *   uncached unbounded fetch matching the pre-Phase-2 contract.
   * - Pre-Phase-1 server (no `maxPropertyEventID` field) → cache skipped,
   *   raw response returned.
   *
   * Pass a `range` produced by `utilsService.timespan(period)` so
   * `range.period` is set; bare `{startTime, endTime}` literals still work
   * but get a `:custom:` cache key with no rolling-window trim.
   */
  public getAssetEvents(assetId: number, range: PeriodDateRange): Promise<Asset> {
    // F-06: legacy null/undefined-range tolerance — bypass the cache, behave
    // like the pre-MR uncached fetch.
    if (!range || range.startTime == null || range.endTime == null) {
      return this.fetchAssetEventsUncached(assetId, range);
    }

    const cacheKey = this.windowKey(assetId, range);

    const inFlight = this._eventsInFlight.get(cacheKey);
    if (inFlight) {
      return inFlight;
    }

    const cached = this._eventsCache.get(cacheKey);
    // F-09: property and health cursors are tracked independently. Either side
    // with a positive cursor triggers an incremental fetch for that side; the
    // other side still gets a full slice via startTime/endTime.
    const hasPropertyCursor = !!cached && cached.lastPropertyCursor > 0;
    const hasHealthCursor = !!cached && cached.lastHealthCursor > 0;
    const hasAnyCursor = hasPropertyCursor || hasHealthCursor;

    // PeriodDateRange.startTime/endTime are Unix ms; REST expects seconds.
    const params: any = {
      cmd: 'getAssets',
      includeDisabled: 1,
      includeEvents: 1,
      assetID: assetId,
      startTime: Math.floor(range.startTime / 1000),
      endTime: Math.floor(range.endTime / 1000),
    };

    if (hasAnyCursor) {
      if (hasPropertyCursor) {
        params.propertyEventsSinceID = cached.lastPropertyCursor;
      }
      if (hasHealthCursor) {
        params.healthEventsSinceID = cached.lastHealthCursor;
      }
      params.includePriorState = 0;
    } else {
      params.includePriorState = 1;
    }

    // F-08: build the inner Promise inside try/catch so a synchronous throw
    // from sendMessage rejects rather than escaping the executor, and attach
    // the in-flight cleanup to the OUTER promise so cleanup always runs even
    // when the inner chain was never constructed.
    //
    // R3 F-01: identity-guard the cache write and the cleanup against
    // _eventsInFlight. The earlier presence-only `has(cacheKey)` check let
    // a stale in-flight resolve write its newEntry into _eventsCache when
    // a later getAssetEvents call had re-registered between invalidate and
    // resolve. Compare against the promise *reference* registered for this
    // call so only the current owner can touch the cache or evict itself.
    let promise: Promise<Asset>;
    try {
      promise = new Promise<Asset>((resolve, reject) => {
        this.commsService.sendMessage(params, false, false)
          .then(data => {
            const asset: Asset = data?.result?.assets?.[0] ?? {};

            // F-07: presence-based feature detection. Phase-1+ servers always
            // emit `maxPropertyEventID` (even when zero for an empty-events
            // asset); pre-Phase-1 omits the field entirely.
            const serverSupportsIncremental = 'maxPropertyEventID' in asset;

            if (!serverSupportsIncremental) {
              this.logger.debug(
                `getAssetEvents: server did not return maxPropertyEventID — ` +
                `skipping incremental cache for assetID=${assetId}`
              );
              resolve(asset);
              return;
            }

            // F-04: helpers build fresh inner arrays so callers (and the
            // cache) never share array references.
            const mergedPropertyEvents = this.mergePropertyEvents(
              cached?.propertyEvents ?? {},
              asset.propertyEvents ?? {}
            );
            const mergedHealthEvents = this.mergeHealthEvents(
              cached?.healthEvents ?? {},
              asset.healthEvents ?? {}
            );
            // CVX-6483: stateEvents have no cursor - the server re-sends the window,
            // but without the prior-state row once includePriorState=0, so keep the
            // cached rows (that seed included) and add what is new.
            const mergedStateEvents = this.mergeHealthEvents(
              cached?.stateEvents ?? {},
              asset.stateEvents ?? {}
            );

            // F-02: recompute the rolling-window lower bound from Date.now()
            // minus the original duration instead of using the (aging)
            // range.startTime captured at the first call.
            let finalPropertyEvents = mergedPropertyEvents;
            let finalHealthEvents = mergedHealthEvents;
            let finalStateEvents = mergedStateEvents;
            if (this.isRollingWindow(range)) {
              const windowStartMs = this.rollingWindowStartMs(range);
              finalPropertyEvents = this.trimPropertyEvents(mergedPropertyEvents, windowStartMs);
              finalHealthEvents = this.trimHealthEvents(mergedHealthEvents, windowStartMs);
              finalStateEvents = this.trimHealthEvents(mergedStateEvents, windowStartMs);
            }

            // F-01: on the incremental path the server may legitimately
            // return `propertyValues: {}` (because `includePriorState=0`), so
            // start from the cached values. CVX-6433: then advance them with
            // anything newer in the response, or they freeze at page entry.
            const newPropertyValues = hasAnyCursor
              ? this.overlayLatestValues(
                  cached?.propertyValues ?? {},
                  asset.propertyValues ?? {},
                  asset.propertyEvents ?? {}
                )
              : (asset.propertyValues ?? cached?.propertyValues ?? {});

            const newEntry: AssetEventsCache = {
              propertyEvents: finalPropertyEvents,
              healthEvents: finalHealthEvents,
              stateEvents: finalStateEvents,
              propertyValues: newPropertyValues,
              lastPropertyCursor: asset.maxPropertyEventID ?? cached?.lastPropertyCursor ?? 0,
              lastHealthCursor: asset.maxHealthEventID ?? cached?.lastHealthCursor ?? 0,
              windowKey: cacheKey,
            };
            // R3 F-01: identity guard — write only if WE are still the
            // registered in-flight. Presence alone is unsafe: invalidate +
            // re-register between sendMessage dispatch and resolve would
            // leave a different promise in the slot, and a presence check
            // would happily clobber its data.
            if (this._eventsInFlight.get(cacheKey) === promise) {
              this._eventsCache.set(cacheKey, newEntry);
            }

            // Return-contract unchanged: caller sees a merged Asset.
            resolve({
              ...asset,
              propertyEvents: finalPropertyEvents,
              healthEvents: finalHealthEvents,
              stateEvents: finalStateEvents,
              propertyValues: newEntry.propertyValues,
            });
          })
          .catch(reject);
      });
    } catch (err) {
      promise = Promise.reject(err);
    }

    // F-08 / R3 F-01: cleanup on the outer Promise so it runs whether the
    // inner chain resolved, rejected, or never constructed. The identity
    // guard prevents a stale resolve from evicting a newer call's
    // registration (the inverse of the cache-write race above).
    promise = promise.finally(() => {
      if (this._eventsInFlight.get(cacheKey) === promise) {
        this._eventsInFlight.delete(cacheKey);
      }
    });

    this._eventsInFlight.set(cacheKey, promise);
    return promise;
  }

  /**
   * F-06: pre-MR fetch shape used when `range` is falsy. Matches the legacy
   * behavior (unbounded fetch when no range is supplied) without touching
   * the incremental cache.
   */
  private fetchAssetEventsUncached(
    assetId: number,
    range: PeriodDateRange | null | undefined
  ): Promise<Asset> {
    const params: any = {
      cmd: 'getAssets',
      includeDisabled: 1,
      includeEvents: 1,
      assetID: assetId,
    };
    if (range && range.startTime != null && range.endTime != null) {
      params.startTime = Math.floor(range.startTime / 1000);
      params.endTime = Math.floor(range.endTime / 1000);
      params.includePriorState = 1;
    }
    return new Promise<Asset>((resolve, reject) => {
      this.commsService.sendMessage(params, false, false)
        .then(data => resolve(data?.result?.assets?.[0] ?? {}))
        .catch(reject);
    });
  }

  /**
   * CVX-5984 F-05: drop incremental-events cache entries so the next
   * `getAssetEvents` call rebuilds from a full-window fetch. Intended for
   * callers that suspect their cursor view has diverged from server state
   * (admin backfill, manual refresh, etc.). No automatic call sites in this
   * service; consumers opt in.
   *
   * @param assetId Drop entries for this asset only. Omit to clear all.
   */
  public invalidateAssetEvents(assetId?: number): void {
    // R2 F-1: clear both the resolved cache AND the in-flight registry. The
    // in-flight then-handler also checks _eventsInFlight membership before
    // writing the cache, so a fetch that resolves after invalidation no longer
    // repopulates a stale entry — the failure mode that reintroduced the
    // CVX-5984 prior-state-blank regression when invalidate raced with the
    // initial mount's getAssetEvents.
    if (assetId == null) {
      this._eventsCache.clear();
      this._eventsInFlight.clear();
      return;
    }
    const prefix = `${assetId}:`;
    for (const key of Array.from(this._eventsCache.keys())) {
      if (key.startsWith(prefix)) {
        this._eventsCache.delete(key);
      }
    }
    for (const key of Array.from(this._eventsInFlight.keys())) {
      if (key.startsWith(prefix)) {
        this._eventsInFlight.delete(key);
      }
    }
  }

  // CVX-6228: startTime is Unix ms (the report's start); REST expects seconds.
  // includePriorState keeps the state in effect at the window start, so health/state
  // timelines built from a bounded fetch match the old full-history ones.
  // No endTime on purpose: getAssets bounds observations by updated_at, so an end bound
  // would drop observations with in-window activity that were edited after the window.
  public getDetailedAssets(assetIds: number[], startTime: number): Promise<Asset[]> {
    // getAssets treats an empty list as "no filter" and returns every asset, disabled included
    if (!assetIds?.length) {
      return Promise.resolve([]);
    }
    return new Promise((resolve, reject) => {
      const when = Date.now();
      this.commsService.sendMessage({
        cmd: 'getAssets',
        includeDisabled: 1,
        includeEvents: 1,
        includePriorState: 1,
        startTime: Math.floor(startTime / 1000),
        assets: assetIds,
        sendTime: when
      }, false, false).then(data => {
        resolve(data?.result?.assets || []);
      }).catch((err) => {
        reject(err);
      });
    });
  }

  public updateCache(data, incremental: boolean = false) {
    // CVX-6039 (QA R2 F-01): discriminate incremental vs full-replace via an
    // explicit caller-driven flag, mirroring check-response.service.ts's
    // `updateCache(data, honorLastRequest = true)` style.
    //
    // The earlier state-based discriminator (`this.asset.data &&
    // this.asset.lastRequest`) keyed off persistent CLIENT STATE, but
    // `lastRequest` is set on EVERY call (including the fetchAll dispatcher),
    // so a SECOND fetchAll (cache populated + cursor set) was misclassified as
    // incremental → a FULL payload (no `removed`) got merged instead of
    // replaced → server-deleted/out-of-scope assets persisted as ghosts. The
    // server does not echo an `incremental` marker and omits `removed` when
    // empty (design doc get-assets-incremental.md), so the payload cannot be
    // used to discriminate. The flag instead reflects "did THIS request send
    // incremental:1", independent of client state and payload shape.
    //
    //   incremental=true  : refresh()/liteRefresh() sent incremental:1. Merge
    //                       incoming by assetID and purge
    //                       removed.{disabled,inactive}.
    //   incremental=false : the fetchAll full-payload path (dispatcher passes
    //                       no flag → default false), cold/first fetch, or
    //                       after clearCache(). data.result.assets is the
    //                       complete set, so assets omitted by the server are
    //                       correctly dropped (restores pre-MR hard replace).
    //
    // lastRequest is advanced in BOTH branches (preserves the pre-MR
    // cursor-advance; matches check-response).
    this.asset.lastRequest = data.result.timestamp;

    if (!incremental) {
      // Full replace: drop any server-omitted/deleted assets, matching the
      // pre-MR behavior and check-response's else-branch. Default to [] so a
      // malformed/empty payload never leaves data null.
      this.asset.data = data.result.assets || [];
      this.events.publish('ccs:assetsUpdate');
      return;
    }

    // Incremental path. Null-safe against clearCache() racing with an in-flight
    // refresh (clearCache() sets data = null but lastRequest is reset too, so
    // this is belt-and-suspenders).
    if (!this.asset.data) {
      this.asset.data = [];
    }

    // Merge incoming assets into this.asset.data by assetID. Guard the upsert
    // against null/undefined/''/NaN assetID, AND treat 0 as invalid (QA R1
    // F-04 + R2 F-05/#5). Domain ids are >= 1 (getAssetById already treats
    // 0/falsy as absent), so an id-0 row is malformed. Rejecting it ALSO stops
    // the findIndex coercion (`+null`, `+undefined`, `+''` all === 0) from
    // matching a malformed null-id cache row to an incoming id-0 row.
    const changedIds: number[] = [];
    each(data.result.assets, (incoming: Asset) => {
      const rawId = incoming?.assetID;
      // Reject null/undefined/'' before coercion — `+null` and `+''` are 0
      // (finite), so a Number.isFinite() check alone would let a malformed row
      // through as a ghost id-0 entry. Then reject NaN (e.g. assetID:'abc').
      if (rawId === null || rawId === undefined || (rawId as any) === '') {
        return;
      }
      const incomingId = Number(rawId);
      // Reject non-finite (NaN) and invalid domain ids (<= 0). assetID 0 is not
      // a real asset; admitting it would let it match malformed null-id cache
      // rows via numeric coercion.
      if (!Number.isFinite(incomingId) || incomingId <= 0) {
        return;
      }
      changedIds.push(incomingId);
      const idx = this.asset.data.findIndex(a => +a.assetID === incomingId);
      if (idx >= 0) {
        this.asset.data[idx] = incoming;
      } else {
        this.asset.data.push(incoming);
      }
    });

    // Honor removed.disabled and removed.inactive. The server omits the
    // 'removed' key entirely on full-replace responses, so guard before access.
    const removed = data.result.removed;
    if (removed) {
      const disabledIds = (removed.disabled || []).map(Number);
      const inactiveIds = (removed.inactive || []).map(Number);
      const allRemovedIds = new Set([...disabledIds, ...inactiveIds]);
      if (allRemovedIds.size) {
        this.asset.data = filter(this.asset.data, a => !allRemovedIds.has(+a.assetID));
        changedIds.push(...allRemovedIds);
      }
    }

    // CVX-6529: name the assets that changed so per-asset pages can skip ticks
    // for other assets. The full-replace branch (and other publishers) send no
    // list, which subscribers read as "anything may have changed".
    this.events.publish('ccs:assetsUpdate', changedIds);
  }

  public clearCache() {
    this.asset.lastRequest = null;
    this.asset.data = null;
  }

  public handleAddAsset(fData) {
    fData.cmd = 'addAsset';
    fData.sendTime = Date.now();
    return this.handleRequest(fData);
  }

  public handleUpdateAsset(fData) {
    fData.cmd = 'updateAssets';
    fData.sendTime = Date.now();
    return this.handleRequest(fData);
  }

  handleDeleteAsset(fData) {
    fData.assets = JSON.stringify(fData);
    fData.cmd = 'deleteAssets';
    return this.handleRequest(fData);
  }

  public removeAssets(assetList: string[]): Promise<any> {
    const cmd = {
      cmd: 'deleteAssets',
      assets: JSON.stringify(assetList)
    };

    return this.handleRequest(cmd);
  }

  public duplicateAssets(assetIDs: number[], targetFolderID: number): Promise<any> {
    // The backend (duplicateAssets.cgi) declares its params as `assets` (array)
    // and `folderID`; getParams drops unrecognized keys, so the payload MUST use
    // those exact names — sending assetIDs/targetFolderID makes the call fail
    // with "assets parameter required".
    // Refresh via liteRefresh() (getAssets with includePropertyValues:0): the
    // management/folder list that triggers a copy never renders asset
    // propertyValues, and the SSE ccs:assetsUpdate path already does a lite
    // refresh after a duplicate. Detail/property/reporting views fetch property
    // values themselves, so the lighter cache update is safe here and avoids the
    // heavy default this.refresh().
    return this.handleRequest({
      cmd: 'duplicateAssets',
      assets: assetIDs,
      folderID: targetFolderID
    }, () => this.liteRefresh());
  }


  public fullPath(asset) {
    let ret = this.folderPath(asset);
    if (ret !== '') {
      ret += '/';
    }
    ret += this.getAssetName(asset, true);
    return ret;
  }

  public folderPath(asset) {
    let ret = '';
    if (asset.folderID) {
      ret = this.folderService.getFolderPath(asset.folderID, false, '/', true) as string;
    }
    return ret;
  }

  public getAssetName(asset: Asset | number, translate: boolean): string {
    if (isNumber(asset)) {
      asset = this.getAssetById(asset)?.[0];
      if (!asset) {
        return '';
      }
    }
    if (translate) {
      return this.userService.translateItem(asset, 'name');
    } else {
      return asset.name;
    }
  }

  public getEnabledAssets(): Asset[] {
    return cloneDeep(filter(this.asset.data, { disabledAt: 0 }));
  }

  public getActiveAssets(): Asset[] {
    return filter(this.asset.data, { disabledAt: 0, active: 1 });
  }

  public getAssetById(id: number): Asset[] {
    return filter(this.asset.data, ['assetID', +id]);
  }

  public getAssetsByType(assetTypeIDs?: any) {
    if (assetTypeIDs && isArray(assetTypeIDs)) {
      // find all of these types and return them
      let l = [];
      each(assetTypeIDs, id => {
        const list = filter(this.asset.data, {checkType: +id});
        l = union(l, list);
      });
      // now we have an ARRAY of matching assets; convert it to an object
      return keyBy(l, 'assetID');
    } else {
      return this.asset.data;
    }
  }

  public getAssetsByProperty(propertyID: number) {
    if (propertyID) {
      // find all of these types and return them
      const list = filter(this.asset.data, (item: any) => {
        if (item?.properties) {
          return some(item.properties, { propertyID } );
        }
        return false;
      });
      // now we have an ARRAY of matching assets; convert it to an object
      return keyBy(list, 'assetID');
    } else {
      return this.asset.data;
    }
  }

  public getAssetLocation(assetID: number): number {
    return this.getAssetById(assetID)?.[0]?.location || null;
  }

  public getAssetsByLocations(locationIds: number[] = [], activeOnly = true) {
    const assets = activeOnly ? this.getActiveAssets() : this.getEnabledAssets();

    if (locationIds.length) {
      return filter(assets, (assetItem) => includes(locationIds, assetItem.location));
    } else {
      return assets;
    }
  }

  public getAssetsByZoneId(zoneId: number, activeOnly = true) {
    const assets = activeOnly ? this.getActiveAssets() : this.getEnabledAssets();
    return filter(assets, { zone: zoneId });
  }

  public getAssetsByName(name: string) {
    const assets = this.getActiveAssets();
    return filter(assets, { name });
  }

  public getAssetsByFolder(folderID: number) {
    const assets = this.getActiveAssets();
    return filter(assets, { folderID });
  }

  public assetState(health: number): string {
    const states = [
      'PROPERTY.State_Down',
      'PROPERTY.State_Strained',
      'PROPERTY.State_Up'
    ];

    if (health >= 0 && health <=2) {
      return this.translate.instant(states[health]);
    } else {
      return this.translate.instant('SHARED.Unknown');
    }
  }

  public isAssetNoState(asset: Asset): boolean {
    return asset.monitorHealth === 0;
  }

  public async getFilters(filterID?: number): Promise<any> {
    const params: any = {cmd: 'getFilters', types: [ 'assetDashboard' ]};
    if (filterID) {
      params.filters = [ filterID ];
    }
    await this.loadingService.enable(null, null);

    const [ response, err] = await awaitHandler (this.commsService.sendMessage(params, false, false));
    this.loadingService.disable();
    if (err) {
      return;
    }
    if (response && response?.reqStatus === 'OK') {
      const filters = get(response, 'result.filters', []);
      if (!filters) {
        return;
      }
      if (filterID) {
        const ref = find(filters, { filterID });
        if (ref) {
          return ref;
        } else {
          return;
        }
      } else {
        return filters;
      }
    }
  }

  public async createFilter(selectors: AssetFilterParameters, theTable?: DataTables.Api, tableOptions?: DataTableOptions): Promise<number> {
    const theColumns = [];
    const sortBy = [];

    if (!isUndefined(theTable) && !isUndefined(tableOptions)) {
      const visibility = <unknown>theTable.columns().visible() as DataTables.Api;
      each(visibility.toArray(), (state: boolean, column: number) => {
        if (!state) {
          return;
        }
        const colName = this.getColumnName(tableOptions, column);
        if (colName) {
          theColumns.push(colName);
        }
      });

      const theOrder = theTable.order();
      if (theOrder) {
        // there is some ordering; grab the column names
        each(theOrder, item => {
          const colName = this.getColumnName(tableOptions, +item[0]);
          if (colName) {
            sortBy.push({ id: colName, dir: item[1] });
          }
        });
      }
    }

    const req = {
      cmd: 'addFilter',
      type: 'assetDashboard',
      sortBy,
      columns: theColumns,
      selectors
    };

    // use the new filterID in the URL

    const [res, err] = await awaitHandler(this.commsService.sendMessage(req));
    if (err || res.reqStatus !== 'OK') {
      // creating the filter failed... what do we do?
      return;
    } else {
      return +res.result.filterID;
    }
  }

  private getColumnName(tableOptions: DataTableOptions, col: number) {
    const ref = tableOptions.columns[col];
    if (ref) {
      return ref?.name ?? ref.data;
    } else {
      return '';
    }
  }

  // CVX-5984: merge incoming property events into cached map by event id.
  // Dedup key: String(ev.id) — the server-assigned row id added by Phase 01 T02.
  // If ev.id is absent (pre-Phase 01 server), fall back to `${propId}:${ev.time}`.
  //
  // F-04: returns a NEW outer map with NEW inner arrays per propId; never
  // mutates `cached` or its arrays. Callers and the cache therefore never
  // share inner array references, eliminating the mutation hazard where a
  // previously-resolved Asset's arrays grew silently on a subsequent refresh.
  private mergePropertyEvents(
    cached: IObjectStringKeyMap<IAssetPropertyValue[]>,
    incoming: IObjectStringKeyMap<IAssetPropertyValue[]>
  ): IObjectStringKeyMap<IAssetPropertyValue[]> {
    const result: IObjectStringKeyMap<IAssetPropertyValue[]> = {};
    // Seed with fresh copies of cached arrays.
    for (const propId of Object.keys(cached ?? {})) {
      result[propId] = [...(cached[propId] ?? [])];
    }
    // Layer incoming events on top, dedup by event id (or propId:time fallback).
    for (const propId of Object.keys(incoming ?? {})) {
      const existing = result[propId] ?? [];
      const seen = new Set(existing.map(ev =>
        ev.id != null ? String(ev.id) : `${propId}:${ev.time}`
      ));
      const merged = [...existing];
      for (const ev of incoming[propId] ?? []) {
        const key = ev.id != null ? String(ev.id) : `${propId}:${ev.time}`;
        if (!seen.has(key)) {
          seen.add(key);
          merged.push(ev);
        }
      }
      result[propId] = merged;
    }
    return result;
  }

  // CVX-6433: advance cached propertyValues with an incremental response.
  // A non-empty server value wins when newer; otherwise the newest incoming
  // property event for each property does. Nothing older than the cached
  // record replaces it, and the cached health (from property_health) is kept
  // when the event carries none. propertyValues[0] (asset state, which drives
  // time-in-state) is not taken from events. Returns fresh records; never
  // mutates `cached`.
  private overlayLatestValues(
    cached: IObjectStringKeyMap<IAssetPropertyValue>,
    incomingValues: IObjectStringKeyMap<IAssetPropertyValue>,
    incomingEvents: IObjectStringKeyMap<IAssetPropertyValue[]>
  ): IObjectStringKeyMap<IAssetPropertyValue> {
    const result: IObjectStringKeyMap<IAssetPropertyValue> = { ...cached };
    const apply = (propId: string, candidate: IAssetPropertyValue | undefined) => {
      const current = result[propId];
      if (!candidate || (current && !(candidate.time > current.time))) {
        return;
      }
      const next = { ...candidate };
      if (next.health === undefined && current?.health !== undefined) {
        next.health = current.health;
      }
      result[propId] = next;
    };

    for (const propId of Object.keys(incomingValues ?? {})) {
      apply(propId, incomingValues[propId]);
    }
    for (const propId of Object.keys(incomingEvents ?? {})) {
      if (propId === '0') {
        continue;
      }
      let newest: IAssetPropertyValue | undefined;
      for (const ev of incomingEvents[propId] ?? []) {
        if (!newest || ev.time > newest.time) {
          newest = ev;
        }
      }
      apply(propId, newest);
    }
    return result;
  }

  // CVX-5984: merge incoming health events into cached map by event id.
  // Dedup key: String(ev.id). Fallback: `${metricKey}:${ev.time}`.
  //
  // F-04: returns a NEW outer map with NEW inner arrays per metric key; never
  // mutates `cached` or its arrays.
  // Also merges asset:inUse stateEvents (CVX-6483), which share the id/time shape.
  private mergeHealthEvents<T extends { id?: number; time: number }>(
    cached: IObjectStringKeyMap<T[]>,
    incoming: IObjectStringKeyMap<T[]>
  ): IObjectStringKeyMap<T[]> {
    const result: IObjectStringKeyMap<T[]> = {};
    for (const metricKey of Object.keys(cached ?? {})) {
      result[metricKey] = [...(cached[metricKey] ?? [])];
    }
    for (const metricKey of Object.keys(incoming ?? {})) {
      const existing = result[metricKey] ?? [];
      const seen = new Set(existing.map(ev =>
        ev.id != null ? String(ev.id) : `${metricKey}:${ev.time}`
      ));
      const merged = [...existing];
      for (const ev of incoming[metricKey] ?? []) {
        const key = ev.id != null ? String(ev.id) : `${metricKey}:${ev.time}`;
        if (!seen.has(key)) {
          seen.add(key);
          merged.push(ev);
        }
      }
      result[metricKey] = merged;
    }
    return result;
  }

  // CVX-5984: trim property events older than windowStartMs from all property arrays.
  // event.time is Unix seconds; windowStartMs is Unix milliseconds.
  // Called only for rolling windows (day, week, 30days, etc.).
  //
  // CVX-5984 bugfix: keep the single most-recent prior-state seed per property
  // (the latest event strictly before the window start) so that
  // assetStatusService.buildStates / definePeriodStatesForEmptyPeriods can fill
  // bars for properties that have no events inside the window. Without this
  // seed the bars for those properties are blank even though the server
  // correctly returned a prior-state row via the UNION query.
  private trimPropertyEvents(
    propertyEvents: IObjectStringKeyMap<IAssetPropertyValue[]>,
    windowStartMs: number
  ): IObjectStringKeyMap<IAssetPropertyValue[]> {
    // R1 F-01: single-pass partition in one unit (ms). The previous form used
    // windowStartMs for the in-window filter and windowStartSecs for the
    // before-window filter; the units happen to partition correctly today but
    // a future refactor of one branch could silently break seed retention.
    const result: IObjectStringKeyMap<IAssetPropertyValue[]> = {};
    for (const propId of Object.keys(propertyEvents)) {
      const inWindow: IAssetPropertyValue[] = [];
      let seed: IAssetPropertyValue | undefined;
      for (const ev of propertyEvents[propId]) {
        if (ev.time * 1000 >= windowStartMs) {
          inWindow.push(ev);
        } else if (!seed || ev.time > seed.time) {
          // Preserve the most-recent prior-state seed (latest event before
          // window start) so the renderer can colour the leading portion of
          // the bar.
          seed = ev;
        }
      }
      result[propId] = seed ? [seed, ...inWindow] : inWindow;
    }
    return result;
  }

  // CVX-5984: trim health events older than windowStartMs from all metric arrays.
  // event.time is Unix seconds; windowStartMs is Unix milliseconds.
  //
  // CVX-5984 bugfix: keep the single most-recent prior-state seed per metric
  // key (the latest event strictly before the window start) so that
  // assetStatusService.buildStates / definePeriodStatesForEmptyPeriods can fill
  // bars for properties that have no events inside the window. Without this
  // seed the bars for those properties are blank even though the server
  // correctly returned a prior-state row via the UNION query.
  private trimHealthEvents<T extends { time: number }>(
    healthEvents: IObjectStringKeyMap<T[]>,
    windowStartMs: number
  ): IObjectStringKeyMap<T[]> {
    // R1 F-01: single-pass partition in one unit (ms). See trimPropertyEvents.
    const result: IObjectStringKeyMap<T[]> = {};
    for (const metricKey of Object.keys(healthEvents)) {
      const inWindow: T[] = [];
      let seed: T | undefined;
      for (const ev of healthEvents[metricKey]) {
        if (ev.time * 1000 >= windowStartMs) {
          inWindow.push(ev);
        } else if (!seed || ev.time > seed.time) {
          seed = ev;
        }
      }
      result[metricKey] = seed ? [seed, ...inWindow] : inWindow;
    }
    return result;
  }

  // CVX-5984 F-03: rolling-window period names — these are name-keyed by
  // utilsService.timespan() and slide forward with wall-clock time. Everything
  // else (including 'yesterday', 'lastweek', etc.) is fixed in time.
  // Kept here (private) instead of a separate util so the cache and the
  // rolling-window decision share a single source of truth.
  private static readonly ROLLING_PERIODS: ReadonlySet<string> = new Set([
    'today', 'day', 'allday',
    '7days', '14days', '28days', '30days', '60days', '90days', '180days', '365days',
    'thisweek', 'week',
    'thismonth', 'month',
    'thisquarter', 'quarter',
    'thisyear',
    'thisfiscalquarter', 'thisfiscalyear',
    'all',
  ]);

  /**
   * CVX-5984 F-02/F-03: stable cache key derived from the period NAME when
   * the caller supplies a named range (the common case: caller built `range`
   * via `utilsService.timespan(period)`). Bare `{startTime, endTime}`
   * literals get a per-(start,end) key — usable but caches less aggressively.
   *
   * The name-driven decision avoids the wall-clock heuristic that previously
   * flipped the same range from `:30days` → `:custom:` after 5 minutes idle
   * (which defeated the cache on long-lived tabs).
   */
  private windowKey(assetId: number, range: PeriodDateRange): string {
    if (range.period) {
      return `${assetId}:${range.period}`;
    }
    return `${assetId}:custom:${range.startTime}-${range.endTime}`;
  }

  /**
   * CVX-5984 F-03: a window is rolling iff the caller supplied a named period
   * that appears in ROLLING_PERIODS. Custom ranges and named-but-fixed
   * windows ('yesterday', 'lastweek', etc.) are not rolling and skip trim.
   */
  private isRollingWindow(range: PeriodDateRange): boolean {
    return !!range.period && AssetsService.ROLLING_PERIODS.has(range.period);
  }

  /**
   * CVX-5984 F-02: for a rolling window, the trim-time lower bound advances
   * with wall-clock time. We recompute it from `Date.now()` minus the original
   * duration (`endTime - startTime` captured when the caller built the range).
   *
   * Trade-off note for 'today' / midnight rollover: an open tab past midnight
   * will see a 24-hour rolling slice anchored on `now`, not strictly the
   * calendar day. This is more correct than the previous behavior (which kept
   * yesterday's events forever once midnight passed) and acceptable for the
   * "user leaves a detail page open across midnight" edge case.
   */
  private rollingWindowStartMs(range: PeriodDateRange): number {
    const originalDurationMs = range.endTime - range.startTime;
    return Date.now() - originalDurationMs;
  }
}
